import { useEffect, useRef, useState } from "react";
import { Lock, Delete, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SwipeButton from "@/components/SwipeButton";

const KEY = "ukerja_last_seen";
const elapsed = () => Date.now() - Number(localStorage.getItem(KEY) || Date.now());
const stamp = () => localStorage.setItem(KEY, String(Date.now()));

function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
}

function PinDots({ value, error }) {
  return (
    <div data-testid="pin-dots" className={`flex gap-4 justify-center ${error ? "animate-[shake_0.3s]" : ""}`}>
      {[0, 1, 2, 3].map((i) => <span key={i} className={`w-3.5 h-3.5 rounded-full border-2 border-current transition-colors ${i < value.length ? "bg-current" : ""}`} />)}
    </div>
  );
}

function PinPad({ onDigit, onDelete, dark }) {
  const key = `h-16 w-16 rounded-full text-2xl font-light grid place-items-center transition-colors ${dark ? "bg-white/10 hover:bg-white/20 active:bg-white/30" : "bg-slate-900/5 hover:bg-slate-900/10 active:bg-slate-900/20"}`;
  return (
    <div className="grid grid-cols-3 gap-x-6 gap-y-4 justify-items-center">
      {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => <button key={d} data-testid={`pin-key-${d}`} onClick={() => onDigit(d)} className={key}>{d}</button>)}
      <span />
      <button data-testid="pin-key-0" onClick={() => onDigit("0")} className={key}>0</button>
      <button data-testid="pin-key-delete" onClick={onDelete} className="h-16 w-16 grid place-items-center"><Delete className="w-6 h-6" /></button>
    </div>
  );
}

function PinStage({ dark, onOk, onForgot }) {
  const [pin, setPin] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const digit = async (d) => {
    if (busy || pin.length >= 4) return;
    const next = pin + d;
    setPin(next);
    if (next.length < 4) return;
    setBusy(true);
    try {
      await api.post("/auth/pin/verify", { pin: next });
      onOk();
    } catch (e) {
      setMsg(errMsg(e));
      setPin("");
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-6 w-full">
      <p className="text-center font-semibold">Masukkan PIN</p>
      <PinDots value={pin} error={!!msg} />
      <p data-testid="pin-error" className="text-center text-xs text-red-400 min-h-4">{busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : msg}</p>
      <PinPad dark={dark} onDigit={digit} onDelete={() => setPin(pin.slice(0, -1))} />
      <button data-testid="forgot-pin-btn" onClick={onForgot} className="block mx-auto text-sm font-semibold opacity-80 hover:opacity-100">Lupa PIN?</button>
    </div>
  );
}

function ForgotStage({ dark, onOk, onBack }) {
  const [f, setF] = useState({ password: "", pin: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const field = `w-full h-12 rounded-xl px-4 outline-none ${dark ? "bg-white/10 placeholder:text-white/50" : "bg-white border border-slate-200"}`;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/auth/pin", f); onOk(); } catch (err) { setMsg(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3 w-full" data-testid="forgot-pin-form">
      <p className="text-center font-semibold mb-2">Buat PIN baru dengan password akun</p>
      <input data-testid="forgot-pin-password" type="password" required className={field} placeholder="Password akun" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
      <input data-testid="forgot-pin-new" inputMode="numeric" pattern="\d{4}" maxLength={4} required className={`${field} tracking-[0.5em] font-mono placeholder:tracking-normal placeholder:font-sans`} placeholder="PIN baru (4 angka)" value={f.pin} onChange={(e) => setF({ ...f, pin: e.target.value.replace(/\D/g, "") })} />
      {msg && <p className="text-xs text-red-400">{msg}</p>}
      <button data-testid="forgot-pin-submit" disabled={busy} className="w-full h-12 rounded-full bg-emerald-500 text-white font-bold flex items-center justify-center gap-2">{busy && <Loader2 className="w-4 h-4 animate-spin" />}Simpan & Buka</button>
      <button type="button" onClick={onBack} className="block mx-auto text-sm opacity-80">Kembali</button>
    </form>
  );
}

function LockScreen({ theme, onUnlock }) {
  const now = useClock();
  const { logout, settings } = useAuth();
  const [stage, setStage] = useState("swipe");
  const dark = theme !== "light";
  return (
    <div data-testid="lock-screen" className={`fixed inset-0 z-[100] flex flex-col items-center px-8 pt-16 pb-10 select-none ${dark ? "bg-slate-950 text-white" : "bg-slate-100 text-slate-900"}`}>
      <div className={`absolute inset-0 pointer-events-none ${dark ? "bg-[radial-gradient(ellipse_at_top,rgba(16,185,129,0.25),transparent_60%)]" : "bg-[radial-gradient(ellipse_at_top,rgba(16,185,129,0.18),transparent_60%)]"}`} />
      <div className="relative flex flex-col items-center gap-1">
        <Lock className="w-5 h-5 mb-2 opacity-80" />
        <p className="text-sm font-semibold capitalize opacity-80">{now.toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long" })}</p>
        <p data-testid="lock-clock" className="text-7xl font-light tracking-tight tabular-nums">{now.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }).replace(".", ":")}</p>
        <p className="text-xs opacity-60 mt-1">{settings.app_name || "UKerja"} terkunci</p>
      </div>
      <div className="relative mt-auto w-full max-w-xs">
        {stage === "swipe" && <div className="space-y-4">
          <SwipeButton testid="lock-swipe" label="geser untuk membuka" color={dark ? "bg-white/15 backdrop-blur-md" : "bg-slate-900/70 backdrop-blur-md"} onConfirm={async () => setStage("pin")} />
          <button data-testid="lock-logout-btn" onClick={logout} className="block mx-auto text-xs opacity-60 hover:opacity-100">Keluar dari akun</button>
        </div>}
        {stage === "pin" && <PinStage dark={dark} onOk={onUnlock} onForgot={() => setStage("forgot")} />}
        {stage === "forgot" && <ForgotStage dark={dark} onOk={onUnlock} onBack={() => setStage("pin")} />}
      </div>
    </div>
  );
}

export default function LockGuard() {
  const { user } = useAuth();
  const lock = user?.lock;
  const active = !!(lock?.enabled && lock?.has_pin);
  const limit = (lock?.timeout || 1) * 60000;
  const [locked, setLocked] = useState(false);
  const lockedRef = useRef(false);
  lockedRef.current = locked;

  useEffect(() => {
    if (!active) { setLocked(false); return undefined; }
    if (localStorage.getItem(KEY) && elapsed() > limit) setLocked(true);
    else stamp();
    const onVis = () => {
      if (document.visibilityState === "hidden") { if (!lockedRef.current) stamp(); }
      else if (elapsed() > limit) setLocked(true);
    };
    const iv = setInterval(() => { if (document.visibilityState === "visible" && !lockedRef.current) stamp(); }, 10000);
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onVis); };
  }, [active, limit]);

  if (!active || !locked) return null;
  return <LockScreen theme={lock.theme} onUnlock={() => { stamp(); setLocked(false); }} />;
}
